import { Fragment, useState } from "react";
import { Badge, Card, Modal, MetricCard, nextDueDate, btnPrimary, btnSecondary, btnDanger, inputStyle, labelStyle, statusColor } from "./shared";

export default function Payments({
  tenants, payments, settings, paymentEdits, session,
  can,
  collected, overdueAmt,
  collapsedMonths, setCollapsedMonths,
  showAddPayment, setShowAddPayment,
  newPayment, setNewPayment, addPayment,
  newPaymentError, setNewPaymentError,
  quickPayError, setQuickPayError,
  invoiceModal, setInvoiceModal,
  invoiceView, setInvoiceView,
  invoiceResult, setInvoiceResult,
  invoiceSending, sendVia, setSendVia,
  sendInvoice,
  showEditPayment, setShowEditPayment,
  editPaymentDraft, setEditPaymentDraft,
  editPaymentError, setEditPaymentError,
  editInternalNotes, setEditInternalNotes,
  savePaymentEdit,
  handleMarkPaid, quickCreatePayment, deletePayment, openInvoice,
  autoGeneratePayments, runLateFeeCheck,
  companyProfile,
  paymentAuditLog = [], changePaymentStatus,
  selectedPeriod,
}) {
  const [quickPay, setQuickPay] = useState(null);
  const [quickForm, setQuickForm] = useState({ amount: "", method: "Zelle", paid: "" });
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceType, setInvoiceType] = useState("receipt");
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  // Status-change modal
  const [statusModal, setStatusModal]   = useState(null); // payment object
  const [statusDraft, setStatusDraft]   = useState({ newStatus: "", reason: "" });
  const [statusSaving, setStatusSaving] = useState(false);
  // History drawer (Set of payment ids currently expanded)
  const [historyOpen, setHistoryOpen]   = useState(new Set());

  const fmtSentDate = ts => ts
    ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  const fmtDateTime = ts => ts
    ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";

  const openStatusModal = (p) => {
    setStatusModal(p);
    setStatusDraft({ newStatus: p.status, reason: "" });
  };

  const toggleHistory = (id) => setHistoryOpen(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const auditForPayment = (id) => paymentAuditLog.filter(a => a.payment_id === id);

  const EnvelopeIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );

  // TEMP: complimentary filter removed to allow invoice testing — restore after testing
  // const complimentaryNames = new Set(tenants.filter(t => t.is_complimentary).map(t => t.name));
  const visiblePayments = payments;

  // Active period — driven by the global month selector
  const now = new Date();
  const thisMonthKey = selectedPeriod || `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
  const dueDay = String(settings.dueDayOfMonth || 1).padStart(2, "0");
  const thisMonthDue = `${thisMonthKey}-${dueDay}`;

  const activePaying = tenants.filter(t =>
    (t.status === "active" || t.status === "month-to-month") &&
    !t.is_complimentary && t.tenant_type !== "non_paying"
  );
  const thisMonthRecords = visiblePayments.filter(p => p.due?.startsWith(thisMonthKey));
  const recordedTenants = new Set(thisMonthRecords.map(p => p.tenant));
  const unpaidTenants = activePaying.filter(t => !recordedTenants.has(t.name));

  const thisMonthRows = [
    ...unpaidTenants.map(t => ({ type: "unpaid", tenant: t })),
    ...thisMonthRecords.filter(p => p.status !== "paid").map(p => ({ type: "record", payment: p })),
    ...thisMonthRecords.filter(p => p.status === "paid").map(p => ({ type: "record", payment: p })),
  ];

  const paidThisMonth = new Set(thisMonthRecords.filter(p => p.status === "paid").map(p => p.tenant));
  const collectedThisMonth = thisMonthRecords.filter(p => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
  const overdueCount = activePaying.filter(t => !paidThisMonth.has(t.name)).length;
  const expectedThisMonth = activePaying.reduce((s, t) => s + (t.rent || 0), 0);
  const lateFeesThisMonth = thisMonthRecords.reduce((s, p) => s + (p.late_fee || 0), 0);

  const payByMonth = {};
  visiblePayments.forEach(p => {
    const key = p.due ? p.due.slice(0, 7) : "0000-00";
    if (!payByMonth[key]) payByMonth[key] = [];
    payByMonth[key].push(p);
  });
  const monthKeys = Object.keys(payByMonth).sort().reverse();
  const pastMonthKeys = monthKeys.filter(k => k !== thisMonthKey);
  const monthLabel = key => {
    if (key === "0000-00") return "No Due Date";
    const [y, m] = key.split("-");
    return new Date(parseInt(y), parseInt(m)-1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };
  const thStyle = { padding: "12px 14px", textAlign: "left", fontSize: 10, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, whiteSpace: "nowrap" };

  const exportCurrentMonth = () => {
    const [yr, mo] = thisMonthKey.split("-");
    const monthName = new Date(parseInt(yr), parseInt(mo) - 1, 1).toLocaleDateString("en-US", { month: "long" });
    const filename = `Leasly-Payments-${monthName}-${yr}.csv`;
    const headers = ["Invoice #","Tenant", "Amount", "Late Fee", "Method", "Due Date", "Paid Date", "Status"];
    const rows = thisMonthRecords.map(p => [
      `"${(p.invoice_number || "").replace(/"/g, '""')}"`,
      `"${(p.tenant || "").replace(/"/g, '""')}"`,
      p.amount ?? "",
      p.late_fee ?? "",
      `"${(p.payment_method || "").replace(/"/g, '""')}"`,
      p.due || "",
      p.paid || "",
      p.status || "",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Invoice search results
  const searchQuery = invoiceSearch.trim().toLowerCase();
  const searchResults = searchQuery
    ? visiblePayments.filter(p =>
        (p.invoice_number && p.invoice_number.toLowerCase().includes(searchQuery)) ||
        (p.tenant && p.tenant.toLowerCase().includes(searchQuery)) ||
        (p.amount && String(p.amount).includes(searchQuery))
      ).slice(0, 20)
    : [];

  return (
    <>
      {/* ── Payments page ── */}
      <div className="app-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 32, color: "#2B2B2B", margin: 0 }}>Rent Collection</h1>
          <div style={{ color: "#9B9690", marginTop: 4, fontSize: 14 }}>${collectedThisMonth.toLocaleString()} collected · {overdueCount} unpaid · {monthLabel(thisMonthKey)}</div>
        </div>
        <div className="app-btn-group" style={{ display: "flex", gap: 10 }}>
          <button style={btnSecondary} onClick={exportCurrentMonth}>↓ Export</button>
          {can.addPayment && <button style={btnPrimary} onClick={() => { setShowAddPayment(true); setNewPaymentError(""); }}>+ Record Payment</button>}
        </div>
      </div>

      <div className="app-metric-row" style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
        <MetricCard label="Expected" value={`$${expectedThisMonth.toLocaleString()}`} sub={`${activePaying.length} active tenant${activePaying.length !== 1 ? "s" : ""}`} accent="#3b82f6" />
        <MetricCard label="Collected" value={<span style={{ color: "#7FB98E" }}>${collectedThisMonth.toLocaleString()}</span>} sub={`${paidThisMonth.size} payment${paidThisMonth.size !== 1 ? "s" : ""} this month`} accent="#22c55e" />
        <MetricCard label="Unpaid" value={`${overdueCount}`} sub={`of ${activePaying.length} tenant${activePaying.length !== 1 ? "s" : ""} haven't paid`} accent="#ef4444" />
        <MetricCard label="Late Fees" value={`$${lateFeesThisMonth.toLocaleString()}`} sub="This month" accent="#a78bfa" />
      </div>

      {/* ── Invoice Search ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="🔍 Search invoices by number, tenant, or amount…"
            value={invoiceSearch}
            onChange={e => setInvoiceSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 14, width: "100%", boxSizing: "border-box" }}
          />
          {invoiceSearch && (
            <button onClick={() => setInvoiceSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9B9690", fontSize: 16, padding: 0 }}>✕</button>
          )}
        </div>
        {searchQuery && (
          <div style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: "0 0 10px 10px", overflow: "hidden", marginTop: -1 }}>
            {searchResults.length === 0
              ? <div style={{ padding: "14px 16px", color: "#9B9690", fontSize: 13 }}>No matching invoices found.</div>
              : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                      {["Invoice #","Tenant","Amount","Paid","Status",""].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((p, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #F0EDE6" }}>
                        <td style={{ padding: "10px 14px", fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 600, color: "#163C2F" }}>{p.invoice_number || <span style={{ color: "#9B9690" }}>—</span>}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600, fontSize: 13 }}>{p.tenant}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}>${p.amount?.toLocaleString()}</td>
                        <td style={{ padding: "10px 14px", color: "#9B9690", fontSize: 13 }}>{p.paid || "—"}</td>
                        <td style={{ padding: "10px 14px" }}><Badge label={p.status} color={statusColor[p.status] || "#8b949e"} /></td>
                        <td style={{ padding: "10px 14px" }}>
                          {p.status === "paid"
                            ? <button onClick={() => { setInvoiceType("receipt"); openInvoice(p); }} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>Invoice</button>
                            : <button onClick={() => handleMarkPaid(p)} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12 }}>Mark Paid</button>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </div>
        )}
      </div>


      {/* ── Current Month ── */}
      {(() => {
        const isCollapsed = collapsedMonths[thisMonthKey];
        const paidCnt = thisMonthRecords.filter(p => p.status === "paid").length;
        const ovdCnt  = thisMonthRecords.filter(p => p.status === "overdue").length;
        return (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => setCollapsedMonths(s => ({ ...s, [thisMonthKey]: !s[thisMonthKey] }))}
              style={{ width: "100%", background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: isCollapsed ? 10 : "10px 10px 0 0", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 16, color: "#163C2F" }}>{monthLabel(thisMonthKey)}</span>
                <span style={{ fontSize: 11, background: "#eaf4ec", color: "#163C2F", fontWeight: 600, padding: "2px 8px", borderRadius: 999, fontFamily: "'Manrope', sans-serif" }}>Current</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {paidCnt > 0 && <Badge label={`${paidCnt} paid`} color="#22c55e" />}
                  {ovdCnt  > 0 && <Badge label={`${ovdCnt} overdue`} color="#ef4444" />}
                  {unpaidTenants.length > 0 && <Badge label={`${unpaidTenants.length} unpaid`} color="#ef4444" />}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {collectedThisMonth > 0 && <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14, color: "#163C2F" }}>${collectedThisMonth.toLocaleString()} collected</span>}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9B9690" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </button>
            {!isCollapsed && (
              <div className="app-table-scroll" style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 740 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                      {["Tenant", "Amount", "Late Fee", "Method", "Due", "Paid", "Invoice #", "Status", "Action"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {thisMonthRows.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: "20px 14px", color: "#9B9690", textAlign: "center", fontSize: 13 }}>No active tenants found.</td></tr>
                    )}
                    {thisMonthRows.map((row, idx) => {
                      if (row.type === "unpaid") {
                        const t = row.tenant;
                        return (
                          <tr key={`unpaid-${t.name}`} style={{ borderBottom: "1px solid #F0EDE6", background: "#fff9f9" }}>
                            <td style={{ padding: "12px 14px", fontWeight: 600 }}>{t.name}</td>
                            <td style={{ padding: "12px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700, color: "#9B9690" }}>{t.rent ? `$${t.rent.toLocaleString()}` : "—"}</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690" }}>—</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690" }}>—</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690", fontSize: 13 }}>{thisMonthDue}</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690" }}>—</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690" }}>—</td>
                            <td style={{ padding: "12px 14px" }}><Badge label="Unpaid" color="#ef4444" /></td>
                            <td style={{ padding: "12px 14px" }}>
                              {can.addPayment && (
                                <button
                                  onClick={() => { setQuickPay(t); setQuickForm({ amount: String(t.rent || ""), method: "Zelle", paid: new Date().toISOString().slice(0,10) }); setQuickPayError(""); }}
                                  style={{ ...btnPrimary, padding: "5px 10px", fontSize: 12 }}
                                >Mark Paid</button>
                              )}
                            </td>
                          </tr>
                        );
                      }
                      const p = row.payment;
                      return (
                        <Fragment key={p.id || idx}>
                          <tr style={{ borderBottom: "1px solid #F0EDE6", background: p.status === "overdue" ? "#fef9f9" : "transparent" }}>
                            <td style={{ padding: "12px 14px", fontWeight: 600 }}>{p.tenant}</td>
                            <td style={{ padding: "12px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}>${p.amount?.toLocaleString()}</td>
                            <td style={{ padding: "12px 14px", fontFamily: "'Manrope', sans-serif", color: p.late_fee ? "#ef4444" : "#9B9690" }}>{p.late_fee ? `$${p.late_fee.toLocaleString()}` : "—"}</td>
                            <td style={{ padding: "12px 14px" }}>{p.payment_method ? <Badge label={p.payment_method} color="#3b82f6" /> : <span style={{ color: "#9B9690" }}>—</span>}</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690", fontSize: 13 }}>{p.due || "—"}</td>
                            <td style={{ padding: "12px 14px", color: "#9B9690", fontSize: 13 }}>{p.paid || "—"}</td>
                            <td style={{ padding: "12px 14px" }}>
                              <div style={{ fontSize: 11, fontFamily: "'Manrope', sans-serif", color: "#163C2F", fontWeight: 600, lineHeight: 1.3 }}>
                                {p.invoice_number || <span style={{ color: "#9B9690" }}>—</span>}
                              </div>
                              {p.invoice_sent_at && (
                                <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 4, color: "#9B9690" }}>
                                  <EnvelopeIcon />
                                  <span style={{ fontSize: 10, fontFamily: "'Manrope', sans-serif" }}>Sent {fmtSentDate(p.invoice_sent_at)}</span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: "12px 14px" }}><Badge label={p.status} color={statusColor[p.status] || "#8b949e"} /></td>
                            <td style={{ padding: "12px 14px" }}>
                              <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                                {/* Primary action */}
                                {p.status !== "paid"
                                  ? <button onClick={() => handleMarkPaid(p)} style={{ ...btnPrimary, padding: "5px 10px", fontSize: 12 }}>Mark Paid</button>
                                  : p.invoice_sent_at
                                    ? <button onClick={() => { setInvoiceType("receipt"); openInvoice(p); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, color: "#9B9690", borderColor: "#D4CFC7" }}>↻ Resend</button>
                                    : <button onClick={() => { setInvoiceType("receipt"); openInvoice(p); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Invoice</button>
                                }
                                {/* Secondary actions: hidden for unpaid — row should look identical to
                                    never-paid tenants until the payment is marked paid again */}
                                {p.status !== "unpaid" && (<>
                                  {can.editPayment && <button onClick={() => openStatusModal(p)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }} title="Change status with reason">⇄ Status</button>}
                                  {can.editPayment && <button onClick={() => { setShowEditPayment(p); setEditPaymentDraft({ amount: p.amount, due: p.due, payment_method: p.payment_method, status: p.status, paid: p.paid }); setEditInternalNotes(""); setEditPaymentError(""); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Edit</button>}
                                  {can.deletePayment && <button onClick={() => deletePayment(p.id)} style={{ ...btnDanger, padding: "4px 8px" }}>🗑</button>}
                                  {auditForPayment(p.id).length > 0 && (
                                    <button onClick={() => toggleHistory(p.id)} style={{ ...btnSecondary, padding: "4px 9px", fontSize: 11, color: "#B08D57", borderColor: "#B08D5788" }}>
                                      {historyOpen.has(p.id) ? "▲" : "▼"} History ({auditForPayment(p.id).length})
                                    </button>
                                  )}
                                </>)}
                              </div>
                            </td>
                          </tr>
                          {historyOpen.has(p.id) && auditForPayment(p.id).map((a, ai) => (
                            <tr key={`audit-${ai}`} style={{ background: "#FFFBF4" }}>
                              <td colSpan={9} style={{ padding: "8px 14px 8px 28px", fontSize: 12, color: "#9B9690", borderBottom: "1px solid #F5F1EA" }}>
                                <span style={{ color: "#B08D57", fontWeight: 600 }}>{a.old_status} → {a.new_status}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span style={{ color: "#2B2B2B" }}>{a.reason}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span>{fmtDateTime(a.changed_at)}</span>
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Past Months ── */}
      {pastMonthKeys.map(key => {
        const group = payByMonth[key];
        const isCollapsed = collapsedMonths[key];
        const paidCount = group.filter(p => p.status === "paid").length;
        const overdueC  = group.filter(p => p.status === "overdue").length;
        const groupTotal = group.filter(p => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
        return (
          <div key={key} style={{ marginBottom: 16 }}>
            <button
              onClick={() => setCollapsedMonths(s => ({ ...s, [key]: !s[key] }))}
              style={{ width: "100%", background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: isCollapsed ? 10 : "10px 10px 0 0", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 16, color: "#163C2F" }}>{monthLabel(key)}</span>
                <span style={{ fontSize: 12, color: "#9B9690" }}>{group.length} record{group.length !== 1 ? "s" : ""}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {paidCount > 0 && <Badge label={`${paidCount} paid`} color="#22c55e" />}
                  {overdueC  > 0 && <Badge label={`${overdueC} overdue`} color="#ef4444" />}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {groupTotal > 0 && <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14, color: "#163C2F" }}>${groupTotal.toLocaleString()} collected</span>}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9B9690" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </button>
            {!isCollapsed && (
              <div className="app-table-scroll" style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 740 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                      {["Tenant", "Amount", "Late Fee", "Method", "Due", "Paid", "Invoice #", "Status", "Action"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((p, idx) => (
                      <Fragment key={p.id || idx}>
                        <tr style={{ borderBottom: "1px solid #F0EDE6", background: p.status === "overdue" ? "#fef9f9" : "transparent" }}>
                          <td style={{ padding: "12px 14px", fontWeight: 600 }}>{p.tenant}</td>
                          <td style={{ padding: "12px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}>${p.amount?.toLocaleString()}</td>
                          <td style={{ padding: "12px 14px", fontFamily: "'Manrope', sans-serif", color: p.late_fee ? "#ef4444" : "#9B9690" }}>{p.late_fee ? `$${p.late_fee.toLocaleString()}` : "—"}</td>
                          <td style={{ padding: "12px 14px" }}>{p.payment_method ? <Badge label={p.payment_method} color="#3b82f6" /> : <span style={{ color: "#9B9690" }}>—</span>}</td>
                          <td style={{ padding: "12px 14px", color: "#9B9690", fontSize: 13 }}>{p.due || "—"}</td>
                          <td style={{ padding: "12px 14px", color: "#9B9690", fontSize: 13 }}>{p.paid || "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 11, fontFamily: "'Manrope', sans-serif", color: "#163C2F", fontWeight: 600 }}>{p.invoice_number || <span style={{ color: "#9B9690" }}>—</span>}</td>
                          <td style={{ padding: "12px 14px" }}><Badge label={p.status} color={statusColor[p.status] || "#8b949e"} /></td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                              {p.status !== "paid"
                                ? <button onClick={() => handleMarkPaid(p)} style={{ ...btnPrimary, padding: "5px 10px", fontSize: 12 }}>Mark Paid</button>
                                : <button onClick={() => { setInvoiceType("receipt"); openInvoice(p); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Invoice</button>}
                              {p.status !== "unpaid" && (<>
                                {can.editPayment && <button onClick={() => openStatusModal(p)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }} title="Change status with reason">⇄ Status</button>}
                                {can.editPayment && <button onClick={() => { setShowEditPayment(p); setEditPaymentDraft({ amount: p.amount, due: p.due, payment_method: p.payment_method, status: p.status, paid: p.paid }); setEditInternalNotes(""); setEditPaymentError(""); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Edit</button>}
                                {can.deletePayment && <button onClick={() => deletePayment(p.id)} style={{ ...btnDanger, padding: "4px 8px" }}>🗑</button>}
                                {auditForPayment(p.id).length > 0 && (
                                  <button onClick={() => toggleHistory(p.id)} style={{ ...btnSecondary, padding: "4px 9px", fontSize: 11, color: "#B08D57", borderColor: "#B08D5788" }}>
                                    {historyOpen.has(p.id) ? "▲" : "▼"} History ({auditForPayment(p.id).length})
                                  </button>
                                )}
                              </>)}
                            </div>
                          </td>
                        </tr>
                          {historyOpen.has(p.id) && auditForPayment(p.id).map((a, ai) => (
                            <tr key={`audit-${ai}`} style={{ background: "#FFFBF4" }}>
                              <td colSpan={9} style={{ padding: "8px 14px 8px 28px", fontSize: 12, color: "#9B9690", borderBottom: "1px solid #F5F1EA" }}>
                                <span style={{ color: "#B08D57", fontWeight: 600 }}>{a.old_status} → {a.new_status}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span style={{ color: "#2B2B2B" }}>{a.reason}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span>{fmtDateTime(a.changed_at)}</span>
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Modals ── */}

      {/* Quick Mark Paid Modal */}
      {quickPay && (
        <Modal title={`Mark Paid — ${quickPay.name}`} onClose={() => { setQuickPay(null); setQuickPayError(""); }} onSave={async () => { const ok = await quickCreatePayment(quickPay, quickForm.amount, quickForm.method, quickForm.paid); if (ok) setQuickPay(null); }}>
          <div>
            <label style={labelStyle}>Amount ($)</label>
            <input type="number" value={quickForm.amount} onChange={e => setQuickForm(s => ({ ...s, amount: e.target.value }))} style={inputStyle} placeholder="0" />
            {quickPayError && <div style={{ marginTop: 6, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626" }}>{quickPayError}</div>}
          </div>
          <div>
            <label style={labelStyle}>Payment Method</label>
            <select value={quickForm.method} onChange={e => setQuickForm(s => ({ ...s, method: e.target.value }))} style={inputStyle}>
              {["Zelle","Cash","Check","Venmo","Other"].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Date Paid</label>
            <input type="date" value={quickForm.paid} onChange={e => setQuickForm(s => ({ ...s, paid: e.target.value }))} style={inputStyle} />
          </div>
        </Modal>
      )}

      {/* Add Payment Modal */}
      {showAddPayment && (
        <Modal title="Record Payment" onClose={() => { setShowAddPayment(false); setNewPaymentError(""); }} onSave={addPayment}>
          {newPayment.existingId && <div style={{ background: "#eaf4ec", border: "1px solid #214D3A", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#163C2F", fontWeight: 600 }}>Updating existing {newPayment.existingStatus || "unpaid"} record for this month</div>}
          <div>
            <label style={labelStyle}>Tenant</label>
            <select value={newPayment.tenant} onChange={e => {
              const t = tenants.find(t => t.name === e.target.value);
              const now = new Date();
              const monthPrefix = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
              const existing = payments.find(p => p.tenant === e.target.value && (p.status === "unpaid" || p.status === "overdue") && p.due && p.due.startsWith(monthPrefix));
              setNewPayment(s => ({
                ...s, tenant: e.target.value,
                property: existing?.property || t?.property || t?.address || "",
                amount: existing?.amount ? String(existing.amount) : (t?.rent ? String(t.rent) : ""),
                due: existing?.due || `${monthPrefix}-${String(settings.dueDayOfMonth||1).padStart(2,"0")}`,
                status: "paid", paid: new Date().toISOString().slice(0,10),
                late_fee: existing?.late_fee ? String(existing.late_fee) : "",
                existingId: existing?.id || null,
                existingStatus: existing?.status || null,
              }));
            }} style={inputStyle}>
              <option value="">— Select Tenant —</option>
              {tenants.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Property</label>
            <input type="text" value={newPayment.property} onChange={e => setNewPayment(s => ({ ...s, property: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Amount ($)</label>
            <input type="number" value={newPayment.amount} onChange={e => setNewPayment(s => ({ ...s, amount: e.target.value }))} style={inputStyle} />
            {newPaymentError && <div style={{ marginTop: 6, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626" }}>{newPaymentError}</div>}
          </div>
          <div>
            <label style={labelStyle}>Late Fee ($) — leave blank if none</label>
            <input type="number" value={newPayment.late_fee} onChange={e => setNewPayment(s => ({ ...s, late_fee: e.target.value }))} style={inputStyle} placeholder="0" />
          </div>
          <div>
            <label style={labelStyle}>Payment Method</label>
            <select value={newPayment.payment_method} onChange={e => setNewPayment(s => ({ ...s, payment_method: e.target.value }))} style={inputStyle}>
              <option value="Zelle">Zelle</option>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="Venmo">Venmo</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Due Date</label>
            <input type="date" value={newPayment.due} onChange={e => setNewPayment(s => ({ ...s, due: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date Paid</label>
            <input type="date" value={newPayment.paid} onChange={e => setNewPayment(s => ({ ...s, paid: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select value={newPayment.status} onChange={e => setNewPayment(s => ({ ...s, status: e.target.value }))} style={inputStyle}>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={newPayment.notes} onChange={e => setNewPayment(s => ({ ...s, notes: e.target.value }))} style={{ ...inputStyle, height: 72, resize: "vertical" }} placeholder="Any additional notes..." />
          </div>
        </Modal>
      )}

      {/* ── Invoice Modal ── */}
      {invoiceModal && (() => {
        const p = invoiceModal;
        const tenant = tenants.find(t => t.name === p.tenant) || {};
        const nextDue = nextDueDate(p.paid);
        const pEdits = paymentEdits.filter(e => String(e.payment_id) === String(p.id));

        // Monthly statement: all payments for this tenant due in same month
        const monthKey = (p.due || p.paid || "").slice(0, 7);
        const monthlyPayments = payments.filter(pay => pay.tenant === p.tenant && (pay.due || "").startsWith(monthKey));
        const monthlyTotal = monthlyPayments.filter(pay => pay.status === "paid").reduce((s, pay) => s + (pay.amount || 0), 0);

        // Full ledger: all payments for this tenant
        const tenantPayments = payments.filter(pay => pay.tenant === p.tenant).sort((a, b) => (b.due || "").localeCompare(a.due || ""));
        const ledgerTotal = tenantPayments.filter(pay => pay.status === "paid").reduce((s, pay) => s + (pay.amount || 0), 0);

        const printInvoice = () => {
          const el = document.getElementById("invoice-print-area");
          if (!el) return;
          const win = window.open("", "_blank");
          win.document.write(`<!DOCTYPE html><html><head><title>Invoice</title><link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700;800&family=Manrope:wght@400;500;600;700&display=swap" rel="stylesheet"><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:'Manrope',Arial,sans-serif;padding:40px;color:#2B2B2B;background:#fff;}table{width:100%;border-collapse:collapse;}th,td{padding:7px 8px;text-align:left;border-bottom:1px solid #e0dbd0;font-size:12px;}th{font-size:10px;color:#9B9690;text-transform:uppercase;letter-spacing:.06em;}@media print{body{padding:20px;}}</style></head><body>${el.innerHTML}</body></html>`);
          win.document.close();
          setTimeout(() => win.print(), 500);
        };

        const TABS = [["receipt","Receipt"],["statement","Statement"],["ledger","Ledger"],["history","History"]];

        return (
          <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="app-modal-inner" style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: 16, padding: 28, width: 580, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 18, color: "#163C2F" }}>Invoice — {p.tenant}</div>
                  {p.invoice_number && <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2, fontFamily: "'Manrope', sans-serif" }}>#{p.invoice_number}</div>}
                </div>
                <button onClick={() => setInvoiceModal(null)} style={{ ...btnSecondary, padding: "4px 10px" }}>✕</button>
              </div>

              {/* Invoice type tabs */}
              <div style={{ display: "flex", background: "#F5F1EA", borderRadius: 8, padding: 3, marginBottom: 18, width: "fit-content" }}>
                {TABS.map(([v, label]) => (
                  <button key={v} onClick={() => { setInvoiceType(v); if (v !== "history") setInvoiceView("tenant"); else setInvoiceView("internal"); }} style={{ padding: "6px 14px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: invoiceType === v ? "#163C2F" : "transparent", color: invoiceType === v ? "#fff" : "#9B9690", transition: "all 0.15s" }}>{label}</button>
                ))}
              </div>

              <div id="invoice-print-area">

                {/* ── Receipt ── */}
                {invoiceType === "receipt" && (
                  <div style={{ background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    {/* Header: logo + company info left, PAID badge + invoice # right */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #E8E4DC" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {companyProfile?.logo_url
                          ? <img src={companyProfile.logo_url} alt="logo" width="40" height="40" style={{ objectFit: "contain", borderRadius: 6, display: "block" }} />
                          : <div style={{ width: 40, height: 40, minWidth: 40, background: "#163C2F", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: "#7FB98E" }}>
                                {((companyProfile?.company_name || settings.landlordName || "L")[0] || "L").toUpperCase()}
                              </span>
                            </div>
                        }
                        <div>
                          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 15, color: "#163C2F", lineHeight: 1.2 }}>
                            {companyProfile?.company_name || settings.landlordName || "Leasly"}
                          </div>
                          {companyProfile?.dba && <div style={{ fontSize: 10, color: "#9B9690", marginTop: 1 }}>DBA: {companyProfile.dba}</div>}
                          {companyProfile?.address && <div style={{ fontSize: 10, color: "#9B9690", marginTop: 1 }}>{companyProfile.address}</div>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ background: "#22c55e22", color: "#16a34a", border: "1px solid #22c55e44", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>PAID</span>
                        {p.invoice_number && <div style={{ fontSize: 10, color: "#9B9690", marginTop: 4 }}>#{p.invoice_number}</div>}
                      </div>
                    </div>
                    {/* Line items */}
                    {[
                      ["Tenant", p.tenant],
                      ["Property", p.property || tenant?.address || "—"],
                      ["Amount Paid", `$${p.amount?.toLocaleString()}${p.late_fee > 0 ? ` + $${p.late_fee} late fee` : ""}`],
                      ["Payment Method", p.payment_method || "—"],
                      ["Payment Date", p.paid || "—"],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: "#9B9690", fontFamily: "'Manrope', sans-serif" }}>{k}</span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                    {/* Optional invoice footer note */}
                    {companyProfile?.invoice_footer && (
                      <>
                        <div style={{ height: 1, background: "#E8E4DC", margin: "12px 0 10px" }} />
                        <div style={{ fontSize: 10, color: "#9B9690", fontStyle: "italic" }}>{companyProfile.invoice_footer}</div>
                      </>
                    )}
                    {/* Contact footer */}
                    <div style={{ height: 1, background: "#E8E4DC", margin: "12px 0 10px" }} />
                    <div style={{ fontSize: 11, color: "#9B9690" }}>
                      {companyProfile?.email || settings.landlordEmail || ""}
                      {(companyProfile?.phone || settings.landlordPhone) && ` · ${companyProfile?.phone || settings.landlordPhone}`}
                      {companyProfile?.website && ` · ${companyProfile.website}`}
                    </div>
                  </div>
                )}

                {/* ── Monthly Statement ── */}
                {invoiceType === "statement" && (
                  <div style={{ background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <div>
                        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 15, color: "#163C2F" }}>Monthly Statement</div>
                        <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>{p.tenant} · {monthKey ? new Date(monthKey + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "—"}</div>
                      </div>
                      <div style={{ textAlign: "right", fontSize: 10, color: "#9B9690" }}>
                        <div>From: {settings.landlordName}</div>
                        <div>{settings.landlordEmail}</div>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#E8E4DC", marginBottom: 12 }} />
                    {monthlyPayments.length === 0
                      ? <div style={{ color: "#9B9690", fontSize: 13 }}>No payments on record for this month.</div>
                      : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid #E8E4DC" }}>
                              {["Due","Amount","Method","Status","Invoice #"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 10, color: "#9B9690", textTransform: "uppercase" }}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {monthlyPayments.map((pay, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #F0EDE6" }}>
                                <td style={{ padding: "8px 8px", color: "#9B9690" }}>{pay.due || "—"}</td>
                                <td style={{ padding: "8px 8px", fontWeight: 700 }}>${pay.amount?.toLocaleString()}</td>
                                <td style={{ padding: "8px 8px", color: "#9B9690" }}>{pay.payment_method || "—"}</td>
                                <td style={{ padding: "8px 8px" }}><Badge label={pay.status} color={statusColor[pay.status] || "#8b949e"} /></td>
                                <td style={{ padding: "8px 8px", color: "#163C2F" }}>{pay.invoice_number || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    }
                    <div style={{ marginTop: 14, padding: "10px 0 0", borderTop: "1px solid #E8E4DC", display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "#9B9690" }}>Month Total Collected</span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: "#163C2F" }}>${monthlyTotal.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {/* ── Full Ledger ── */}
                {invoiceType === "ledger" && (
                  <div style={{ background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <div>
                        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 15, color: "#163C2F" }}>Full Payment Ledger</div>
                        <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>{p.tenant} · All time</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#163C2F" }}>${ledgerTotal.toLocaleString()}</div>
                        <div style={{ fontSize: 10, color: "#9B9690" }}>Total paid</div>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#E8E4DC", marginBottom: 12 }} />
                    {tenantPayments.length === 0
                      ? <div style={{ color: "#9B9690", fontSize: 13 }}>No payment history.</div>
                      : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid #E8E4DC" }}>
                              {["Due","Paid","Amount","Method","Status","Invoice #"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 10, color: "#9B9690", textTransform: "uppercase" }}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {tenantPayments.map((pay, i) => (
                              <tr key={i} style={{ borderBottom: "1px solid #F0EDE6" }}>
                                <td style={{ padding: "7px 8px", color: "#9B9690" }}>{pay.due || "—"}</td>
                                <td style={{ padding: "7px 8px", color: "#9B9690" }}>{pay.paid || "—"}</td>
                                <td style={{ padding: "7px 8px", fontWeight: 700 }}>${pay.amount?.toLocaleString()}</td>
                                <td style={{ padding: "7px 8px", color: "#9B9690" }}>{pay.payment_method || "—"}</td>
                                <td style={{ padding: "7px 8px" }}><Badge label={pay.status} color={statusColor[pay.status] || "#8b949e"} /></td>
                                <td style={{ padding: "7px 8px", color: "#163C2F" }}>{pay.invoice_number || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    }
                  </div>
                )}

                {/* ── History (edit log) ── */}
                {invoiceType === "history" && (
                  <div style={{ background: "#f5f3ed", border: "1px solid #e0dbd0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Edit History</div>
                    {pEdits.length === 0
                      ? <div style={{ fontSize: 13, color: "#8b949e", fontStyle: "italic" }}>No edits recorded for this payment.</div>
                      : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                              {["When","By","Field","Before","After","Notes"].map(h => <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontSize: 10, color: "#8b949e", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase" }}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {pEdits.map((e, ei) => (
                              <tr key={ei} style={{ borderBottom: "1px solid #ede9e0" }}>
                                <td style={{ padding: "6px 8px", color: "#9B9690", whiteSpace: "nowrap" }}>{new Date(e.edited_at).toLocaleString()}</td>
                                <td style={{ padding: "6px 8px", color: "#9B9690" }}>{session?.user?.email || e.edited_by}</td>
                                <td style={{ padding: "6px 8px", fontWeight: 600 }}>{e.field_changed}</td>
                                <td style={{ padding: "6px 8px", color: "#ef4444", fontFamily: "'Manrope', sans-serif" }}>{e.old_value || "—"}</td>
                                <td style={{ padding: "6px 8px", color: "#22c55e", fontFamily: "'Manrope', sans-serif" }}>{e.new_value || "—"}</td>
                                <td style={{ padding: "6px 8px", color: "#9B9690", fontStyle: "italic" }}>{e.internal_notes || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    }
                  </div>
                )}
              </div>

              {/* Send section — only on Receipt tab */}
              {invoiceType === "receipt" && (
                <>
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ ...labelStyle, marginBottom: 10 }}>Send via</div>
                    <div style={{ display: "flex", gap: 10 }}>
                      {[{ key: "email", label: "📧 Email", detail: tenant.email }].map(({ key, label, detail }) => (
                        <button key={key} onClick={() => setSendVia(s => ({ ...s, [key]: !s[key] }))} style={{ flex: 1, padding: "12px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left", border: `2px solid ${sendVia[key] ? "#163C2F" : "#D4CFC7"}`, background: sendVia[key] ? "#eaf4ec" : "#FFFFFF", color: sendVia[key] ? "#163C2F" : "#9B9690" }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{label}</div>
                          <div style={{ fontSize: 11, marginTop: 3, opacity: 0.75 }}>{detail || "No contact on file"}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {invoiceResult && invoiceResult.map((r, i) => (
                    <div key={i} style={{ background: r.success ? "#22c55e18" : "#ef444418", border: `1px solid ${r.success ? "#22c55e44" : "#ef444444"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 8, fontSize: 13, color: r.success ? "#22c55e" : "#ef4444" }}>
                      {r.success ? "✅" : "❌"} <strong>{r.channel}</strong>: {r.msg}
                    </div>
                  ))}
                </>
              )}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setInvoiceModal(null)} style={btnSecondary}>Close</button>
                <button onClick={printInvoice} style={btnSecondary}>🖨️ Print</button>
                {invoiceType === "receipt" && (
                  invoiceResult
                    ? <button onClick={() => setInvoiceResult(null)} style={btnSecondary}>Resend</button>
                    : <button onClick={() => setShowSendConfirm(true)} disabled={!sendVia.email} style={{ ...btnPrimary, opacity: !sendVia.email ? 0.5 : 1, minWidth: 130 }}>Send Invoice</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Send Invoice Confirm Modal ────────────────────────────────────────── */}
      {showSendConfirm && invoiceModal && (() => {
        const p   = invoiceModal;
        const tenant = tenants.find(t => t.name === p.tenant) || {};
        const co  = companyProfile?.company_name || settings?.landlordName || "Leasly";
        const monthLabel = p.due
          ? new Date(p.due + "T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })
          : p.paid
            ? new Date(p.paid + "T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })
            : "—";
        const nextDue = nextDueDate(p.paid);
        const channels = [sendVia.email && tenant.email && `Email → ${tenant.email}`].filter(Boolean);

        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: "#FFFFFF", border: "1px solid #E8E4DC", borderRadius: 16, padding: 32, width: "100%", maxWidth: 480, boxShadow: "0 12px 48px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" }}>

              {/* Header */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 22, color: "#2B2B2B", marginBottom: 4 }}>Confirm & Send Invoice</div>
                <div style={{ fontSize: 13, color: "#9B9690" }}>Review the invoice below before sending.</div>
              </div>

              {/* Invoice preview card */}
              <div style={{ background: "#F5F1EA", border: "1px solid #E8E4DC", borderRadius: 12, padding: "20px 22px", marginBottom: 22 }}>

                {/* Company header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid #E8E4DC" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {companyProfile?.logo_url
                      ? <img src={companyProfile.logo_url} alt="logo" width="36" height="36" style={{ objectFit: "contain", borderRadius: 6 }}/>
                      : <div style={{ width: 36, height: 36, background: "#163C2F", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "#7FB98E" }}>L</span>
                        </div>
                    }
                    <div>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 15, color: "#163C2F" }}>{co}</div>
                      {companyProfile?.address && <div style={{ fontSize: 11, color: "#9B9690", marginTop: 1 }}>{companyProfile.address}</div>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ background: "#22c55e22", color: "#16a34a", border: "1px solid #22c55e44", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>PAID</span>
                    {p.invoice_number && <div style={{ fontSize: 10, color: "#9B9690", marginTop: 4 }}>#{p.invoice_number}</div>}
                  </div>
                </div>

                {/* Invoice rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {[
                    ["Tenant",           p.tenant || "—"],
                    ["Property",         p.property || tenant.address || "—"],
                    ["Payment Period",    monthLabel],
                    ["Amount",           `$${Number(p.amount || 0).toLocaleString()}${p.late_fee > 0 ? ` + $${p.late_fee} late fee` : ""}`],
                    ["Due Date",         p.due || "—"],
                    ["Paid Date",        p.paid || "—"],
                    ["Payment Method",   p.payment_method || "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 12, color: "#9B9690", fontFamily: "'Manrope', sans-serif" }}>{k}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#2B2B2B" }}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #E8E4DC", fontSize: 11, color: "#9B9690" }}>
                  From: <span style={{ color: "#163C2F", fontWeight: 600 }}>{companyProfile?.email || settings?.landlordEmail || "—"}</span>
                  {(companyProfile?.phone || settings?.landlordPhone) && <> · {companyProfile?.phone || settings?.landlordPhone}</>}
                </div>
              </div>

              {/* Send channels summary */}
              <div style={{ background: "#EAF2ED", border: "1px solid #C5DBC9", borderRadius: 8, padding: "10px 14px", marginBottom: 22, fontSize: 13, color: "#163C2F" }}>
                <strong>Sending via:</strong> {channels.length > 0 ? channels.join(" and ") : "No channel selected"}
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button onClick={() => setShowSendConfirm(false)} style={btnSecondary}>Cancel</button>
                <button
                  onClick={() => { setShowSendConfirm(false); sendInvoice(); }}
                  disabled={invoiceSending || channels.length === 0}
                  style={{ ...btnPrimary, opacity: (invoiceSending || channels.length === 0) ? 0.5 : 1, minWidth: 150 }}
                >
                  {invoiceSending ? "Sending…" : "Confirm & Send"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Change Status Modal ── */}
      {statusModal && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 16, padding: 28, width: 420, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20, color: "#2B2B2B", marginBottom: 6 }}>Change Payment Status</div>
            <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 20 }}>
              {statusModal.tenant} · {statusModal.due ? new Date(statusModal.due + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"} · ${statusModal.amount?.toLocaleString()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Current status</label>
                <div style={{ padding: "9px 13px", background: "#F5F1EA", border: "1px solid #E8E4DC", borderRadius: 8, fontSize: 14, fontWeight: 600, color: "#2B2B2B", textTransform: "capitalize" }}>{statusModal.status}</div>
              </div>
              <div>
                <label style={labelStyle}>New status</label>
                <select value={statusDraft.newStatus} onChange={e => setStatusDraft(s => ({ ...s, newStatus: e.target.value }))} style={inputStyle}>
                  {["paid", "unpaid", "overdue"].map(s => (
                    <option key={s} value={s} disabled={s === statusModal.status}>{s.charAt(0).toUpperCase() + s.slice(1)}{s === statusModal.status ? " (current)" : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Reason for change <span style={{ color: "#dc2626" }}>*</span></label>
                <textarea value={statusDraft.reason} onChange={e => setStatusDraft(s => ({ ...s, reason: e.target.value }))}
                  placeholder="e.g. Marked wrong tenant as paid, correcting to unpaid"
                  style={{ ...inputStyle, height: 80, resize: "vertical" }} />
                {statusSaving && !statusDraft.reason.trim() && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>Reason is required.</div>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
              <button onClick={() => { setStatusModal(null); setStatusSaving(false); }} style={btnSecondary}>Cancel</button>
              <button
                disabled={statusSaving || statusDraft.newStatus === statusModal.status}
                onClick={async () => {
                  setStatusSaving(true);
                  if (!statusDraft.reason.trim()) return;
                  const ok = await changePaymentStatus(statusModal.id, statusModal.status, statusDraft.newStatus, statusDraft.reason);
                  if (ok) setStatusModal(null);
                  setStatusSaving(false);
                }}
                style={{ ...btnPrimary, opacity: (statusSaving || statusDraft.newStatus === statusModal.status) ? 0.5 : 1 }}
              >{statusSaving ? "Saving…" : "Save Change"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {showEditPayment && (
        <Modal title="Edit Payment" onClose={() => { setShowEditPayment(null); setEditPaymentError(""); }} onSave={savePaymentEdit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={labelStyle}>Amount ($)</label>
              <input type="number" value={editPaymentDraft.amount ?? ""} onChange={e => setEditPaymentDraft(s => ({ ...s, amount: e.target.value }))} style={inputStyle} placeholder="0" />
              {editPaymentError && <div style={{ marginTop: 6, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626" }}>{editPaymentError}</div>}
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={editPaymentDraft.status ?? ""} onChange={e => setEditPaymentDraft(s => ({ ...s, status: e.target.value }))} style={inputStyle}>
                {["paid","unpaid","overdue","waived"].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input type="date" value={editPaymentDraft.due ?? ""} onChange={e => setEditPaymentDraft(s => ({ ...s, due: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Date Paid</label>
              <input type="date" value={editPaymentDraft.paid ?? ""} onChange={e => setEditPaymentDraft(s => ({ ...s, paid: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Payment Method</label>
              <select value={editPaymentDraft.payment_method ?? ""} onChange={e => setEditPaymentDraft(s => ({ ...s, payment_method: e.target.value }))} style={inputStyle}>
                <option value="">— Select —</option>
                {["Cash","Check","Zelle","Venmo","ACH","Credit Card","Money Order","Other"].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Internal Notes (reason for edit)</label>
              <textarea value={editInternalNotes} onChange={e => setEditInternalNotes(e.target.value)} style={{ ...inputStyle, height: 72, resize: "vertical" }} placeholder="Optional — explain why this edit was made" />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
